import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, isDevMode, NgZone, Renderer2, RendererFactory2 } from "@angular/core";
import { filter, first, ReplaySubject, takeWhile, timer } from "rxjs";
import { ConfigService } from "../config/config.service";
import { PlatformService } from "../platform/platform.service";
import { GoogleAnalyticsService } from "ngx-google-analytics";

@Injectable({
  providedIn: "root",
})
/**
 * La clase `DataLayerService` proporciona métodos para configurar y gestionar Google Tag Manager (GTM).
 */
export class DataLayerService {
  private renderer2: Renderer2;
  private constantesGlobales;
  private GTMReady = new ReplaySubject<boolean>(1);
  private scrollListener: () => void;
  private timeoutId: NodeJS.Timeout;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private configService: ConfigService,
    private renderFactory2: RendererFactory2,
    private platformService: PlatformService,
    private ngZone: NgZone,
    private gaService: GoogleAnalyticsService,
  ) {
    this.constantesGlobales = this.configService.getConstantesGlobales();
    this.renderer2 = this.renderFactory2.createRenderer(null, null);
    this.setGTMconfig();
  }

  /**
   * Configura Google Tag Manager.
   * Si se cumple la condición de usar GTM y no retardar su carga, se inicializa de forma normal.
   * Si se cumple la condición de usar GTM y retardar su carga, se inicializa después de un tiempo de espera y al hacer scroll.
   * @returns void
   */
  private setGTMconfig(): void {
    if (this.platformService.isBrowser()) {
      this.configService.getConfigPrensa().subscribe((result) => {
        if (Boolean(result?.general["global-constants"]?.usar_gtm) === true) {
          this.normalGTMInitialization(500);
        }
      });
    }
  }

  /**
   * Inicializa Google Tag Manager (GTM) de forma normal con un timeout.
   * Si no se encuentra en modo de desarrollo, se ejecuta fuera del contexto de Angular
   * después de un tiempo de espera especificado.
   *
   * @param timeout El tiempo de espera en milisegundos antes de ejecutar la inicialización de GTM.
   */
  private normalGTMInitialization(timeout: number = 500): void {
    if (!isDevMode()) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.inicializarGTM();
          this.checkGTMReady();
        }, timeout);
      });
    }
  }

  get gtmReady$() {
    return this.GTMReady.asObservable();
  }

  /**
   * Envía un evento de vista de página a Google Analytics si la plataforma es un navegador y el GTM (Google Tag Manager) está listo.
   *
   * Este método verifica si la plataforma actual es un navegador usando `platformService.isBrowser()`. Si es verdadero, espera a que el GTM esté listo suscribiéndose al observable `gtmReady$`. Una vez que el GTM está listo, envía la URL de la página actual (excluyendo los parámetros de consulta) al servicio de Google Analytics.
   *
   * @returns {void}
   */
  sendPageView(): void {
    if (this.platformService.isBrowser()) {
      if (this.gtmReady$) {
        this.gtmReady$.pipe(first()).subscribe((isReady) => {
          if (isReady) {
            this.gaService.pageView(window.location.href.split("?")[0]);
          }
        });
      }
    }
  }

  private pingHome(obj) {
    if (this.platformService.isBrowser()) {
      if (obj) window["dataLayer"].push(obj);
    }
  }

  logAdsRefresh() {
    if (this.platformService.isBrowser()) {
      if (this.gtmReady$) {
        this.gtmReady$.pipe(first()).subscribe((isReady) => {
          if (isReady) {
            const hit = {
              event: "adsrefresh",
              pageUrl: window.location.href.split("?")[0],
              pageTitle: document.title,
            };
            this.pingHome(hit);
          }
        });
      }
    }
  }

  /**
   * Inicializa Google Tag Manager (GTM) inyectando dinámicamente los scripts necesarios en el documento.
   * Este método solo debe ser llamado en un entorno de navegador.
   *
   * - Añade una etiqueta script en el head del documento para cargar el JavaScript de GTM.
   * - Añade una etiqueta noscript con un iframe en el body del documento para soporte noscript de GTM.
   *
   * @remarks
   * Este método utiliza el servicio Renderer2 para crear y manipular elementos del DOM.
   * Verifica si la plataforma es un navegador antes de proceder con la inyección del script.
   *
   * @returns {void}
   */
  inicializarGTM(): void {
    // Primer script
    if (this.platformService.isBrowser()) {
      const script1 = this.renderer2.createElement("script");
      this.renderer2.setAttribute(script1, "defer", "");
      script1.id = "gtm-script";
      script1.text = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        console.log("loaded GTM");
        })(window,document,'script','dataLayer','${this.constantesGlobales.tagGTM}');
      `;
      this.renderer2.appendChild(this.document.head, script1);

      // Segundo script
      const noscript = this.renderer2.createElement("noscript");
      noscript.id = "gtm-noscript";
      const iframe = this.renderer2.createElement("iframe");
      this.renderer2.setAttribute(iframe, "src", `https://www.googletagmanager.com/ns.html?id=${this.constantesGlobales.tagGTM}`);
      this.renderer2.setAttribute(iframe, "height", "0");
      this.renderer2.setAttribute(iframe, "width", "0");
      this.renderer2.setStyle(iframe, "display", "none");
      this.renderer2.setStyle(iframe, "visibility", "hidden");
      this.renderer2.appendChild(noscript, iframe);
      this.renderer2.appendChild(this.document.body, noscript);
    }
  }

  /**
   * Verifica si Google Tag Manager (GTM) está listo comprobando la presencia del objeto `dataLayer` en la ventana del navegador.
   * Si se encuentra el `dataLayer`, emite un valor `true` al sujeto `GTMReady` y completa el sujeto.
   * Este método solo se ejecuta en un entorno de navegador.
   *
   * @remarks
   * - Utiliza RxJS `timer` para comprobar periódicamente la existencia del objeto `dataLayer`.
   * - Se suscribe al temporizador y filtra las emisiones hasta que `dataLayer` esté definido.
   * - Se desuscribe del temporizador una vez que se encuentra `dataLayer`.
   */
  checkGTMReady(): void {
    if (this.platformService.isBrowser()) {
      const checkInterval = timer(0, 100);
      if (checkInterval) {
        const subscription = checkInterval
          .pipe(
            filter(() => typeof window["dataLayer"] !== "undefined"),
            takeWhile(() => !window["dataLayer"], true),
          )
          .subscribe(() => {
            this.GTMReady.next(true);
            this.GTMReady.complete();
            subscription.unsubscribe();
          });
      }
    }
  }
}
